import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { FaBolt, FaWrench, FaPaintRoller, FaHammer, FaSnowflake, FaBroom, FaWindowMaximize, FaHome, FaCheckCircle, FaMapMarkerAlt } from 'react-icons/fa';
import WorkerCard from '../components/WorkerCard';
import BookingForm from '../components/BookingForm';
import { getUserLocation, getProvidersByUrgency, formatDistance, URGENCY_MODES } from '../services/locationService';
import './ServiceDetailPage.css';

const ServiceDetailPage = () => {
    const { serviceName } = useParams();
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [professionals, setProfessionals] = useState([]);
    const [displayPros, setDisplayPros] = useState([]);
    const [loadingPros, setLoadingPros] = useState(true);

    // Location-based urgency
    const [urgencyMode, setUrgencyMode] = useState(null); // null = not chosen yet
    const [userLocation, setUserLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState('');

    const handleBookWorker = (workerData) => {
        setSelectedWorker(workerData);
        setShowBookingForm(true);
    };

    // Fetch real providers for this service from Firestore
    useEffect(() => {
        const fetchPros = async () => {
            setLoadingPros(true);
            try {
                const q = query(
                    collection(db, 'serviceProviders'),
                    where('serviceCategory', '==', serviceName)
                );
                const displayLabel = serviceName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                const q2 = query(
                    collection(db, 'serviceProviders'),
                    where('serviceCategory', '==', displayLabel)
                );
                const [snap1, snap2] = await Promise.all([getDocs(q), getDocs(q2)]);
                const seenIds = new Set();
                const data = [];
                [...snap1.docs, ...snap2.docs].forEach(d => {
                    if (!seenIds.has(d.id)) { seenIds.add(d.id); data.push({ id: d.id, ...d.data() }); }
                });
                setProfessionals(data);
                setDisplayPros(data);
            } catch (e) {
                console.error('Failed to load professionals:', e);
            } finally {
                setLoadingPros(false);
            }
        };
        if (serviceName) fetchPros();
    }, [serviceName]);

    // Apply urgency-based sorting when mode or location changes
    const applySorting = useCallback((mode, location, pros) => {
        if (!location || !mode) {
            setDisplayPros(pros);
            return;
        }
        const sorted = getProvidersByUrgency(pros, location.latitude, location.longitude, mode);
        setDisplayPros(sorted);
    }, []);

    // Handle urgency mode selection — always fetch fresh location
    const handleUrgencySelect = async (mode) => {
        setUrgencyMode(mode);
        setLocationError('');
        setLocationLoading(true);

        try {
            const loc = await getUserLocation();
            setUserLocation(loc);
            applySorting(mode, loc, professionals);
        } catch (err) {
            setLocationError(err.message);
            setDisplayPros(professionals); // fallback: show unsorted
        } finally {
            setLocationLoading(false);
        }
    };

    // Reset to show all
    const handleResetMode = () => {
        setUrgencyMode(null);
        setDisplayPros(professionals);
    };

    const serviceIcons = {
        'electrician': <FaBolt />,
        'plumber': <FaWrench />,
        'painter': <FaPaintRoller />,
        'carpenter': <FaHammer />,
        'ac-repair': <FaSnowflake />,
        'cleaning': <FaBroom />,
        'glass-windows': <FaWindowMaximize />,
        'home-renovation': <FaHome />
    };

    const serviceIcon = serviceIcons[serviceName] || <FaBolt />;
    const displayName = serviceName ? serviceName.split('-').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') : 'Electrician';

    const serviceDetails = {
        description: 'Professional services for your home and office. Our verified professionals handle everything from installations to repairs with expertise and safety.',
        features: [
            'Licensed & Insured Professionals',
            'Same-Day Service Available',
            'Free Consultation & Estimates',
            '90-Day Service Warranty',
            'Emergency Services 24/7'
        ],
        pricing: [
            { service: 'Basic Service', price: '₹500 - ₹1000' },
            { service: 'Standard Installation', price: '₹1000 - ₹2500' },
            { service: 'Repair Work', price: '₹500 - ₹1500' },
            { service: 'Complete Service', price: '₹15,000 - ₹40,000' }
        ]
    };

    return (
        <div className="service-detail-page">
            <div className="container">
                <div className="service-header">
                    <div className="service-header-content">
                        <div className="service-icon-large">{serviceIcon}</div>
                        <div>
                            <h1>{displayName} Services</h1>
                            <p className="service-meta">
                                <span>⭐ 4.8 Average Rating</span>
                                <span>•</span>
                                <span>1200+ Verified Professionals</span>
                                <span>•</span>
                                <span>10,000+ Services Completed</span>
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowBookingForm(true)}
                    >
                        Book Now
                    </button>
                </div>

                <section className="service-info">
                    <h2>About This Service</h2>
                    <p>{serviceDetails.description}</p>

                    <h3>What's Included</h3>
                    <ul className="features-list">
                        {serviceDetails.features.map((feature, index) => (
                            <li key={index}>
                                <span className="check-icon"><FaCheckCircle /></span>
                                {feature}
                            </li>
                        ))}
                    </ul>

                    <h3>Pricing Guide</h3>
                    <div className="pricing-table">
                        {serviceDetails.pricing.map((item, index) => (
                            <div key={index} className="pricing-row">
                                <span className="service-name">{item.service}</span>
                                <span className="service-price">{item.price}</span>
                            </div>
                        ))}
                    </div>
                    <p className="pricing-note">
                        * Prices may vary based on complexity and location. Final price will be confirmed by the professional.
                    </p>
                </section>

                {/* ── Urgency / Location Picker ── */}
                <section className="urgency-section">
                    <h2>How urgent is your need?</h2>
                    <p className="urgency-subtitle">We'll find the best professional based on your preference</p>

                    <div className="urgency-cards">
                        <button
                            className={`urgency-card urgency-card--immediate ${urgencyMode === URGENCY_MODES.IMMEDIATE ? 'active' : ''}`}
                            onClick={() => handleUrgencySelect(URGENCY_MODES.IMMEDIATE)}
                        >
                            <span className="urgency-icon">⚡</span>
                            <strong>I need help now</strong>
                            <p>Find the nearest available professional immediately</p>
                            {urgencyMode === URGENCY_MODES.IMMEDIATE && <span className="urgency-check">✓</span>}
                        </button>

                        <button
                            className={`urgency-card urgency-card--flexible ${urgencyMode === URGENCY_MODES.FLEXIBLE ? 'active' : ''}`}
                            onClick={() => handleUrgencySelect(URGENCY_MODES.FLEXIBLE)}
                        >
                            <span className="urgency-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#3b82f6" />
                                    <circle cx="12" cy="9" r="2.5" fill="#ffffff" />
                                </svg>
                            </span>
                            <strong>I can wait</strong>
                            <p>Find the best-rated professionals from your area</p>
                            {urgencyMode === URGENCY_MODES.FLEXIBLE && <span className="urgency-check">✓</span>}
                        </button>
                    </div>

                    {locationLoading && (
                        <div className="location-status">
                            <div className="location-spinner"></div>
                            <span>Getting your location…</span>
                        </div>
                    )}

                    {locationError && (
                        <div className="location-error">
                            <FaMapMarkerAlt /> {locationError}
                        </div>
                    )}

                    {urgencyMode && userLocation && (
                        <div className="location-active">
                            <FaMapMarkerAlt />
                            <span>
                                Showing {urgencyMode === URGENCY_MODES.IMMEDIATE ? 'nearest professionals' : 'professionals in your area'}
                            </span>
                            <button className="reset-mode-btn" onClick={handleResetMode}>Show All</button>
                        </div>
                    )}
                </section>

                <section className="professionals-section">
                    <h2>Available Professionals</h2>
                    {loadingPros ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Loading professionals…</div>
                    ) : displayPros.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                            {urgencyMode
                                ? 'No professionals found nearby. Try "Show All" to see everyone.'
                                : 'No professionals registered for this service yet. Check back soon!'}
                        </div>
                    ) : (
                        <div className="professionals-grid">
                            {displayPros.map((worker) => (
                                <WorkerCard
                                    key={worker.id}
                                    name={worker.name}
                                    profession={displayName}
                                    rating={worker.rating ? worker.rating.toFixed(1) : '—'}
                                    completedJobs={worker.completedJobs || 0}
                                    hourlyRate={worker.hourlyRate || 0}
                                    skills={worker.skills || []}
                                    verified={worker.isVerified || false}
                                    distance={worker.distance && worker.distance !== Infinity ? formatDistance(worker.distance) : null}
                                    onBookNow={handleBookWorker}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {showBookingForm && (
                <BookingForm
                    serviceName={displayName}
                    workerData={selectedWorker}
                    onClose={() => setShowBookingForm(false)}
                    urgencyMode={urgencyMode}
                    userLocation={userLocation}
                />
            )}
        </div>
    );
};

export default ServiceDetailPage;
